import { useState, useEffect } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  query,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  addDoc,
  where,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { addLog } from "../../utils/audit";
import { scheduleReturnReminder, createRequestApprovalNotification } from "../../services/notifications";

export default function RequestApproval() {
  const [requests, setRequests] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [updatingId, setUpdatingId] = useState(null);
  const [totalReturns, setTotalReturns] = useState(0);

  useEffect(() => {
    // Load all asset requests
    const q = query(collection(db, "requests"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt,
          lastUpdated: data.lastUpdated,
        };
      });
      setRequests(requestsData);
      setTotalReturns(requestsData.filter(req => req.status === "returned").length);
      setLoading(false);
    });

    // Load technicians
    const loadTechnicians = async () => {
      try {
        const usersQuery = query(collection(db, "users"));
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTechnicians(usersData.filter((u) => u.role === "technician"));
      } catch (error) {
        console.error("Error loading technicians:", error);
      }
    };

    // Load total returned assets (requests with status "returned")
    const loadTotalReturns = async () => {
      try {
        const returnsQuery = query(
          collection(db, "requests"),
          where("status", "==", "returned")
        );
        const returnsSnap = await getDocs(returnsQuery);
        setTotalReturns(returnsSnap.size);
      } catch (error) {
        console.error("Error counting returned requests:", error);
        setTotalReturns(0);
      }
    };

    loadTechnicians();
    loadTotalReturns();
    return () => unsubscribe();
  }, []);

  // Safe date formatting for mobile
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      return new Date(timestamp.toDate ? timestamp.toDate() : timestamp).toLocaleDateString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  const handleApproval = async (requestId, status, notes = "") => {
    setUpdatingId(requestId);
    try {
      const requestRef = doc(db, "requests", requestId);
      const request = requests.find((r) => r.id === requestId);

      const updateData = {
        status: status,
        lastUpdated: serverTimestamp(),
      };

      if (notes) updateData.notes = notes;

      await updateDoc(requestRef, updateData);

      // Handle different request types
      if (status === "approved") {
        if (request.type === "borrow" && request.assetId && request.assetId !== 'N/A') {
          // Get current asset data before updating to store original location
          const assetDoc = await getDoc(doc(db, "assets", request.assetId));
          let originalLocation = null;
          let originalLocationName = null;
          let originalRoom = null;
          if (assetDoc.exists()) {
            const assetData = assetDoc.data();
            originalLocation = assetData.location || null;
            originalLocationName = assetData.currentLocation || null;
            originalRoom = assetData.room || null;

            // Store original location in the request for restoration during return
            await updateDoc(requestRef, {
              originalLocation: originalLocation,
            });
          }

          // Determine new location from request destination
          const destinationRoomId = request.destinationId || request.destinationRoom;
          const destinationRoomName = request.destinationName;

          // Update asset status and location for borrow requests
          const assetRef = doc(db, "assets", request.assetId);
          const assetUpdateData = {
            status: "in-use",
            lastUpdated: serverTimestamp(),
          };

          if (destinationRoomId) {
            assetUpdateData.location = destinationRoomId;
            assetUpdateData.room = destinationRoomId;
          }

          if (destinationRoomName) {
            assetUpdateData.currentLocation = destinationRoomName;
          }

          // Store original location fields in asset for restoration during return
          if (originalLocation !== null) assetUpdateData.originalLocation = originalLocation;
          if (originalLocationName !== null) assetUpdateData.originalLocationName = originalLocationName;
          if (originalRoom !== null) assetUpdateData.originalRoom = originalRoom;

          await updateDoc(assetRef, assetUpdateData);

          // Schedule return reminder notification
          await scheduleReturnReminder(request);
        } else if (request.type === "return" && request.assetId && request.assetId !== 'N/A') {
          // Get asset data to restore original location
          const assetDoc = await getDoc(doc(db, "assets", request.assetId));
          let originalData = {};
          if (assetDoc.exists()) {
            const assetData = assetDoc.data();
            // Restore original location fields if they exist
            if (assetData.originalLocation !== undefined) {
              originalData.location = assetData.originalLocation;
              originalData.room = assetData.originalRoom || assetData.originalLocation;
            }
            if (assetData.originalLocationName !== undefined) {
              originalData.currentLocation = assetData.originalLocationName;
            }
            // Clear original fields after restoration
            originalData.originalLocation = null;
            originalData.originalLocationName = null;
            originalData.originalRoom = null;
          }

          // Update asset status for return requests - set back to available and restore location
          const assetRef = doc(db, "assets", request.assetId);
          await updateDoc(assetRef, {
            status: "available",
            lastUpdated: serverTimestamp(),
            returnedAt: serverTimestamp(),
            returnedBy: request.requesterId || null,
            ...originalData,
          });
        } else if (request.type === "maintenance" && request.assignedTo) {
          // Update asset status to "under maintenance" so it's not available for borrowing
          const assetRef = doc(db, "assets", request.assetId);
          await updateDoc(assetRef, {
            status: "under maintenance",
            maintenanceRequestId: requestId,
            lastUpdated: serverTimestamp(),
          });

          // Create maintenance task for maintenance requests
          const technician = technicians.find((t) => t.id === request.assignedTo);
          const maintenanceTaskData = {
            requestId: requestId,
            assetId: request.assetId, // Add the asset ID for QR scanning
            assignedTechnician: request.assignedTo,
            technicianName: technician?.name || "Technician",
            assetName: request.assetName || "Unknown Asset",
            description: request.reason || "Maintenance required",
            status: "pending", // Technician will change this to "in-progress" when they start
            priority: request.urgency === "Critical" ? "high" :
                     request.urgency === "High" ? "medium" : "low",
            location: request.destinationName || "TBD",
            type: "maintenance",
            createdAt: serverTimestamp(),
            assignedAt: serverTimestamp(),
            requesterName: request.requesterName || "Unknown",
            requesterId: request.requesterId || null,
          };

          await addDoc(collection(db, "maintenanceTasks"), maintenanceTaskData);

          await addLog("admin", `Created maintenance task for technician: ${technician?.name}`, {
            requestId,
            technicianId: request.assignedTo,
            technicianName: technician?.name,
            taskId: "created",
          });
        }

        // Create approval notification for student (for all approval types)
        await createRequestApprovalNotification(request);

        // Delete the admin notification for this request (borrow requests only, since they were handled)
        if (request.type === "borrow") {
          try {
            // Note: We can't query for the notification without inequality, so we rely on user clicking to delete
            console.log("Admin notification will be deleted when clicked");
          } catch (error) {
            console.error("Error deleting admin notification:", error);
          }
        }
      }

      await addLog(
        "admin",
        `${status} ${request.type} request for ${request.assetName} by ${request.requesterName}`,
        {
          requestId,
          assetName: request.assetName,
          requesterName: request.requesterName,
          newStatus: status,
          requestType: request.type,
        }
      );

      alert(`✅ ${request.type.charAt(0).toUpperCase() + request.type.slice(1)} request ${status} successfully!`);
    } catch (err) {
      console.error("Error updating request:", err);
      alert("❌ Failed to update request: " + err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const confirmMaintenanceCompletion = async (requestId) => {
    setUpdatingId(requestId);
    try {
      const request = requests.find((r) => r.id === requestId);

      // Find the maintenance task
      const tasksQuery = query(
        collection(db, "maintenanceTasks"),
        where("requestId", "==", requestId)
      );
      const tasksSnap = await getDocs(tasksQuery);

      if (!tasksSnap.empty) {
        const taskDoc = tasksSnap.docs[0];
        const taskRef = doc(db, "maintenanceTasks", taskDoc.id);

        // Mark task as completed
        await updateDoc(taskRef, {
          status: "completed",
          confirmedBy: "admin",
          confirmedAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });

        // Update asset status back to available
        const assetRef = doc(db, "assets", request.assetId);
        await updateDoc(assetRef, {
          status: "available",
          maintenanceRequestId: null,
          lastUpdated: serverTimestamp(),
        });

        // Update request status to completed
        const requestRef = doc(db, "requests", requestId);
        await updateDoc(requestRef, {
          status: "completed",
          lastUpdated: serverTimestamp(),
        });

        await addLog("admin", `Confirmed maintenance completion for ${request.assetName}`, {
          requestId,
          assetId: request.assetId,
          assetName: request.assetName,
          taskId: taskDoc.id,
        });

        alert("✅ Maintenance completion confirmed! Asset is now available.");
      }
    } catch (err) {
      console.error("Error confirming maintenance completion:", err);
      alert("❌ Failed to confirm maintenance completion: " + err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const assignToTechnician = async (requestId, technicianId) => {
    if (!technicianId) return;

    setUpdatingId(requestId);
    try {
      const requestRef = doc(db, "requests", requestId);
      const technician = technicians.find((t) => t.id === technicianId);

      // Just assign the technician, don't change status or create task yet
      await updateDoc(requestRef, {
        assignedTo: technicianId,
        assignedToName: technician?.name || "Technician",
        lastUpdated: serverTimestamp(),
        // Keep status as "pending" until admin clicks "Approve"
      });

      await addLog("admin", `Assigned technician ${technician?.name} to maintenance request`, {
        requestId,
        technicianId,
        technicianName: technician?.name,
      });

      alert("✅ Technician assigned! Click 'Approve' to send the maintenance task.");
    } catch (err) {
      console.error("Error assigning technician:", err);
      alert("❌ Failed to assign technician: " + err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredRequests = filter === "all"
    ? requests
    : filter === "returned"
    ? requests.filter((req) => req.status === "returned")
    : requests.filter((req) => req.status === filter);

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800 border border-yellow-300";
      case "approved": return "bg-green-100 text-green-800 border border-green-300";
      case "rejected": return "bg-red-100 text-red-800 border border-red-300";
      case "completed": return "bg-blue-100 text-blue-800 border border-blue-300";
      case "in-progress": return "bg-purple-100 text-purple-800 border border-purple-300";
      default: return "bg-gray-100 text-gray-800 border border-gray-300";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "borrow": return "📦";
      case "return": return "↩️";
      case "maintenance": return "🛠️";
      default: return "📋";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen">
      <div className="pt-16 p-4">
        {/* Filter Tabs */}
        <div className="flex overflow-x-auto gap-2 mb-4 pb-2 -mx-4 px-4">
          {[
            { key: "all", label: "All", count: requests.length },
            { key: "pending", label: "Pending", count: requests.filter(r => r.status === "pending").length },
            { key: "approved", label: "Approved", count: requests.filter(r => r.status === "approved").length },
            { key: "in-progress", label: "In Progress", count: requests.filter(r => r.status === "in-progress").length },
            { key: "awaiting-confirmation", label: "Awaiting Confirmation", count: requests.filter(r => r.status === "awaiting-confirmation").length },
            { key: "completed", label: "Completed", count: requests.filter(r => r.status === "completed").length },
            { key: "returned", label: "Returned Assets", count: totalReturns },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                filter === tab.key
                  ? "bg-orange-600 text-white shadow-md"
                  : "bg-white text-gray-700 border border-gray-300"
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Requests List */}
        <div className="space-y-3">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-300">
              <div className="text-gray-400 text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {filter === "all" ? "" : filter} requests
              </h3>
              <p className="text-gray-500 text-sm">
                {filter === "pending" ? "All requests processed" : "No matching requests"}
              </p>
            </div>
          ) : (
            filteredRequests.map((request) => (
              <div
                key={request.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 max-w-4xl mx-auto"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{getTypeIcon(request.type)}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-base">
                        {request.assetName}
                      </h3>
                      <p className="text-xs text-gray-600">
                        {formatDate(request.createdAt)}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(request.status)}`}>
                    {request.status}
                  </span>
                </div>

                {/* Student Profile Info */}
                {request.type === "borrow" && (
                  <div className="bg-blue-50 p-3 rounded-lg mb-3">
                    <h4 className="font-medium text-blue-900 mb-2 text-sm">Student Information</h4>
                    <div className="space-y-1 text-xs md:text-sm text-blue-800">
                      <div>
                        <span>Name: </span>
                        <span className="font-medium">
                          {request.studentFirstName && request.studentSurname
                            ? `${request.studentFirstName} ${request.studentMiddleName || ''} ${request.studentSurname}`.trim()
                            : request.requesterName || 'Not available'}
                        </span>
                      </div>
                      {request.studentLrn && (
                        <div>
                          <span>LRN: </span>
                          <span className="font-medium">{request.studentLrn}</span>
                        </div>
                      )}
                      {request.studentGradeYearLevel && (
                        <div>
                          <span>Grade/Year: </span>
                          <span className="font-medium">{request.studentGradeYearLevel}</span>
                        </div>
                      )}
                      {request.studentSection && (
                        <div>
                          <span>Section: </span>
                          <span className="font-medium">{request.studentSection}</span>
                        </div>
                      )}
                      {request.studentContactNumber && (
                        <div>
                          <span>Contact: </span>
                          <span className="font-medium">{request.studentContactNumber}</span>
                        </div>
                      )}
                    </div>

                    {/* Scheduling Information */}
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <h5 className="font-medium text-blue-900 mb-2 text-sm">Scheduling Details</h5>
                      <div className="space-y-1 text-xs md:text-sm text-blue-800">
                        <div>
                          <span>Borrowing Date: </span>
                          <span className="font-medium">{request.borrowingDate || 'Not specified'}</span>
                        </div>
                        <div>
                          <span>Time to Borrow: </span>
                          <span className="font-medium">{request.borrowTime || 'Not specified'}</span>
                        </div>
                        <div>
                          <span>Time to Return: </span>
                          <span className="font-medium">{request.returnTime || 'Not specified'}</span>
                        </div>
                        <div>
                          <span>Submitted: </span>
                          <span className="font-medium">
                            {request.createdAt ? new Date(request.createdAt.toDate ? request.createdAt.toDate() : request.createdAt).toLocaleString() : 'Unknown'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Maintenance Requester Info */}
                {request.type === "maintenance" && (
                  <div className="bg-orange-50 p-3 rounded-lg mb-3">
                    <h4 className="font-medium text-orange-900 mb-2 text-sm">Maintenance Request Details</h4>
                    <div className="space-y-1 text-xs md:text-sm text-orange-800">
                      <div>
                        <span>Requested By: </span>
                        <span className="font-medium">
                          {request.requesterName || 'Unknown Requester'}
                        </span>
                      </div>
                      {request.requesterId && (
                        <div>
                          <span>Requester ID: </span>
                          <span className="font-medium">{request.requesterId}</span>
                        </div>
                      )}
                      <div>
                        <span>Submitted: </span>
                        <span className="font-medium">
                          {request.createdAt ? new Date(request.createdAt.toDate ? request.createdAt.toDate() : request.createdAt).toLocaleString() : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Details */}
                <div className="space-y-2 text-xs md:text-sm text-gray-700">
                  <div>
                    <span>Type: </span>
                    <span className="font-medium capitalize">{request.type}</span>
                  </div>
                  <div>
                    <span>Urgency: </span>
                    <span className={`font-medium ${
                      request.urgency === "High" ? "text-orange-600" :
                      request.urgency === "Critical" ? "text-red-600" : "text-gray-600"
                    }`}>
                      {request.urgency}
                    </span>
                  </div>
                  {request.destinationName && (
                    <div>
                      <span>Move To: </span>
                      <span className="font-medium text-blue-600">{request.destinationName}</span>
                    </div>
                  )}
                </div>

                {/* Reason */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {request.reason}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 pt-3 border-t border-gray-100">
                  {request.status === "pending" && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleApproval(request.id, "approved")}
                        disabled={updatingId === request.id}
                        className="flex-1 bg-green-500 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors disabled:opacity-50"
                      >
                        {updatingId === request.id ? "..." : "Approve"}
                      </button>
                      <button
                        onClick={() => {
                          const notes = prompt("Reason for rejection:");
                          if (notes?.trim()) handleApproval(request.id, "rejected", notes);
                        }}
                        disabled={updatingId === request.id}
                        className="flex-1 bg-red-500 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
                      >
                        Reject
                      </button>
                      {request.type === "maintenance" && (
                        <select
                          onChange={(e) => assignToTechnician(request.id, e.target.value)}
                          disabled={updatingId === request.id}
                          className="flex-1 border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Assign Tech</option>
                          {technicians.map((tech) => (
                            <option key={tech.id} value={tech.id}>
                              {tech.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {request.status === "in-progress" && (
                    <button
                      onClick={() => handleApproval(request.id, "completed")}
                      disabled={updatingId === request.id}
                      className="w-full bg-blue-500 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
                    >
                      {updatingId === request.id ? "Completing..." : "Mark Complete"}
                    </button>
                  )}

                  {request.status === "awaiting-confirmation" && request.type === "maintenance" && (
                    <>
                      <button
                        onClick={() => confirmMaintenanceCompletion(request.id)}
                        disabled={updatingId === request.id}
                        className="w-full bg-green-500 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors disabled:opacity-50"
                      >
                        {updatingId === request.id ? "Confirming..." : "Confirm Maintenance Complete"}
                      </button>

                      {/* Technician Completion Details */}
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <h5 className="font-medium text-green-900 mb-3 text-sm">🛠️ Technician Work Summary</h5>
                        <div className="space-y-2 text-sm">
                          {request.technicianNotes && (
                            <div>
                              <span className="font-medium text-green-800">Technician Notes:</span>
                              <p className="mt-1 text-green-700 bg-white p-2 rounded border text-xs">{request.technicianNotes}</p>
                            </div>
                          )}

                          {request.laborHours && (
                            <div className="flex justify-between">
                              <span className="font-medium text-green-800">Labor Hours:</span>
                              <span className="text-green-700">{request.laborHours} {request.laborHours === 1 ? 'hour' : 'hours'}</span>
                            </div>
                          )}

                          {request.partsUsed && (
                            <div>
                              <span className="font-medium text-green-800">Parts Used:</span>
                              <p className="mt-1 text-green-700 bg-white p-2 rounded border text-xs">{request.partsUsed}</p>
                            </div>
                          )}

                          {request.completedAt && (
                            <div className="flex justify-between">
                              <span className="font-medium text-green-800">Completed:</span>
                              <span className="text-green-700 text-xs">{formatDate(request.completedAt)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {request.notes && (
                    <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                      <p className="text-xs text-yellow-800">
                        <span className="font-medium">Note:</span> {request.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
